import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormInput } from '../components/FormControls.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const TIMEOUTS = [
  { value: 30000, label: '30 seconds' },
  { value: 120000, label: '2 minutes' },
  { value: 300000, label: '5 minutes' },
  { value: 600000, label: '10 minutes' },
];

const KIND_LABEL = {
  engine: 'ViibCMS',
  site: 'Site',
  other: 'App',
};

function scriptEntries(pkg) {
  const scripts = pkg?.scripts;
  if (!scripts || typeof scripts !== 'object') {
    return [];
  }
  return Object.entries(scripts).filter((entry) => typeof entry[0] === 'string' && typeof entry[1] === 'string');
}

function scriptSpec(pkg, name) {
  const spec = pkg?.scriptParams?.[name];
  if (!spec || typeof spec !== 'object') {
    return null;
  }
  return {
    waitsForInput: Boolean(spec.waitsForInput),
    params: Array.isArray(spec.params) ? spec.params : [],
  };
}

function humanize(name) {
  return String(name || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function collectArgs(params, values) {
  const args = [];
  const display = [];
  for (const param of params || []) {
    const raw = values?.[param.name];
    if (!param.value) {
      if (!raw) {
        continue;
      }
      const flag = param.flags?.[0] || `--${param.name}`;
      args.push(flag);
      display.push(flag);
      continue;
    }
    const text = String(raw ?? '').trim();
    if (!text) {
      continue;
    }
    if (param.flags?.length) {
      args.push(param.flags[0], text);
      display.push(param.flags[0], param.secret ? '••••' : text);
    } else {
      args.push(text);
      display.push(param.secret ? '••••' : text);
    }
  }
  return { args, display };
}

function missingRequired(params, values) {
  return (params || []).filter((param) => {
    if (!param.required) {
      return false;
    }
    if (!param.value) {
      return !values?.[param.name];
    }
    return String(values?.[param.name] ?? '').trim() === '';
  });
}

function siteHost(url) {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return String(url || '')
      .replace(/^[a-z]+:\/\//i, '')
      .split('/')[0]
      .split(':')[0]
      .toLowerCase();
  }
}

function siteStage(url) {
  const host = siteHost(url);
  if (!host) {
    return null;
  }
  if (host === 'viib.ca' || host.endsWith('.viib.ca')) {
    return 'staging';
  }
  return 'live';
}

function versionTitle(version) {
  if (!version) {
    return '';
  }
  return [version.detail, version.branch ? `branch ${version.branch}` : '', version.remoteShort ? `remote ${version.remoteShort}` : '']
    .filter(Boolean)
    .join(' · ');
}

function VersionControl({ label, version, updating, disabled, onUpdate }) {
  if (!version) {
    return null;
  }
  const latest = version.color === 'blue';
  const text = `${label} ${version.short || version.status || 'unknown'}`;
  const title = versionTitle(version);
  if (version.canUpdate) {
    return (
      <button type="button" className="npm-version-btn is-behind" title={title} disabled={disabled} onClick={onUpdate}>
        {updating ? 'Updating…' : `Update ${text}`}
      </button>
    );
  }
  return <span className={`npm-version-chip ${latest ? 'is-latest' : 'is-stuck'}`} title={title}>{text}</span>;
}

export function AdminPm2AppsPage() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [root, setRoot] = useState('');
  const [apps, setApps] = useState([]);
  const [versions, setVersions] = useState({});
  const [loading, setLoading] = useState(true);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [error, setError] = useState('');
  const [versionError, setVersionError] = useState('');
  const [message, setMessage] = useState('');
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [health, setHealth] = useState({});
  const [sitesLoading, setSitesLoading] = useState(true);
  const [openFolder, setOpenFolder] = useState('');
  const [drafts, setDrafts] = useState({});
  const [timeoutMs, setTimeoutMs] = useState(120000);
  const [runningKey, setRunningKey] = useState('');
  const [output, setOutput] = useState(null);

  const loadApps = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await apiJson('/api/admin/pm2/projects');
      const data = list?.data ?? {};
      setRoot(typeof data.root === 'string' ? data.root : '');
      setApps(Array.isArray(data.projects) ? data.projects : []);
    } catch (e) {
      setError(e.message);
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHealth = useCallback(async () => {
    setSitesLoading(true);
    try {
      const list = await apiJson('/api/admin/pm2/projects/health');
      const rows = Array.isArray(list?.data?.projects) ? list.data.projects : [];
      const next = {};
      for (const row of rows) {
        if (row?.folder) {
          next[row.folder] = row;
        }
      }
      setHealth(next);
    } catch {
      setHealth({});
    } finally {
      setSitesLoading(false);
    }
  }, []);

  const loadVersions = useCallback(async () => {
    setVersionsLoading(true);
    setVersionError('');
    try {
      const list = await apiJson('/api/admin/pm2/projects/versions');
      const rows = Array.isArray(list?.data?.projects) ? list.data.projects : [];
      const next = {};
      for (const row of rows) {
        if (row?.folder) {
          next[row.folder] = row;
        }
      }
      setVersions(next);
    } catch (e) {
      setVersionError(e.message);
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadApps();
    void loadVersions();
    void loadHealth();
  }, [loadApps, loadVersions, loadHealth]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return [...apps]
      .filter((app) => {
        const kind = app.packageJson?.kind || 'other';
        if (kindFilter !== 'all' && kind !== kindFilter) {
          return false;
        }
        const stage = siteStage(app.siteUrl);
        if (stageFilter !== 'all' && stage !== stageFilter) {
          return false;
        }
        if (!needle) {
          return true;
        }
        const folder = String(app.folder || '').toLowerCase();
        const name = String(app.packageJson?.name || '').toLowerCase();
        const host = siteHost(app.siteUrl);
        return folder.includes(needle) || name.includes(needle) || host.includes(needle);
      })
      .sort((a, b) => {
        const rank = { engine: 0, site: 1, other: 2 };
        const left = rank[a.packageJson?.kind] ?? 2;
        const right = rank[b.packageJson?.kind] ?? 2;
        if (left !== right) {
          return left - right;
        }
        return String(a.folder).localeCompare(String(b.folder));
      });
  }, [apps, query, kindFilter, stageFilter]);

  const setParam = (folder, script, name, value) => {
    const key = `${folder}::${script}`;
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [name]: value },
    }));
  };

  const runScript = async (folder, script, params) => {
    const values = drafts[`${folder}::${script}`] || {};
    const missing = missingRequired(params, values);
    if (missing.length) {
      setMessage('');
      setError(`${script} needs ${missing.map((param) => humanize(param.name)).join(', ')}.`);
      return;
    }
    const { args, display } = collectArgs(params, values);
    if (args.length > 20) {
      setError('Too many arguments.');
      return;
    }
    const timeoutLabel = TIMEOUTS.find((item) => item.value === timeoutMs)?.label || `${timeoutMs} ms`;
    const extra = display.length ? ` -- ${display.join(' ')}` : '';
    if (!window.confirm(`Run npm run ${script}${extra} in ${folder}? This waits until it exits (up to ${timeoutLabel}).`)) {
      return;
    }

    const key = `npm:${folder}:${script}`;
    setRunningKey(key);
    setError('');
    setMessage('');
    setOutput(null);
    try {
      const body = { script, timeoutMs };
      if (args.length) {
        body.args = args;
      }
      const result = await apiJson(`/api/admin/pm2/projects/${encodeURIComponent(folder)}/npm`, {
        method: 'POST',
        body,
      });
      setOutput({
        title: `${folder} · npm run ${script}`,
        ...(result?.data || {}),
      });
      setMessage(`npm run ${script} finished in ${folder}.`);
    } catch (e) {
      const failed = e.body?.data;
      if (failed && typeof failed === 'object') {
        setOutput({ title: `${folder} · npm run ${script}`, ...failed });
      }
      setError(e.message);
    } finally {
      setRunningKey('');
    }
  };

  const updateVersion = async (folder, target, version) => {
    const label = target === 'engine' ? 'ViibCMS engine' : 'site repository';
    const remote = version?.remoteShort || 'the latest commit';
    if (!window.confirm(`Update the ${label} for ${folder} to ${remote}?`)) {
      return;
    }
    const key = `version:${folder}:${target}`;
    setRunningKey(key);
    setError('');
    setMessage('');
    setOutput(null);
    try {
      const result = await apiJson(`/api/admin/pm2/projects/${encodeURIComponent(folder)}/version`, {
        method: 'POST',
        body: { target },
      });
      if (result?.data?.folder) {
        setVersions((prev) => ({ ...prev, [result.data.folder]: result.data }));
      }
      const next = target === 'engine' ? result?.data?.engine : result?.data?.project;
      setMessage(`${folder} ${label} is now ${next?.short || remote}.`);
    } catch (e) {
      const failed = e.body?.data;
      if (failed && (failed.stdout || failed.stderr)) {
        setOutput({
          title: `${folder} · update ${label}`,
          stdout: failed.stdout,
          stderr: failed.stderr,
        });
      }
      setError(e.message);
    } finally {
      setRunningKey('');
    }
  };

  if (!caps.pm2) {
    return (
      <div className="page">
        <p className="err">PM2 is not included in your current plan.</p>
      </div>
    );
  }

  const busy = Boolean(runningKey);

  return (
    <div className="page">
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
        <div>
          <h1 style={{ marginBottom: '0.25rem' }}>npm apps</h1>
          <p className="muted small" style={{ margin: 0 }}>
            Run a package.json script, or update a site and its ViibCMS engine.{' '}
            <Link to="/admin/pm2">PM2 processes →</Link>
            {' · '}
            <Link to="/admin/integrations">Integration settings →</Link>
          </p>
          {root ? (
            <p className="muted small" style={{ marginTop: '0.35rem' }}>
              Root {root}
              {versionsLoading ? ' · checking git versions…' : ''}
              {sitesLoading ? ' · checking sites…' : ''}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => {
            void loadApps();
            void loadVersions();
            void loadHealth();
          }}
          disabled={loading || busy}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error ? <p className="err">{error}</p> : null}
      {versionError ? <p className="err">{versionError}</p> : null}
      {message ? <p className="ok">{message}</p> : null}

      <div className="d-flex gap-2 flex-wrap align-items-end" style={{ marginTop: '0.75rem' }}>
        <label className="field" style={{ margin: 0, minWidth: '16rem', flex: '1 1 16rem' }}>
          <span className="small">Find</span>
          <FormInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Folder or package name" />
        </label>
        <label className="field" style={{ margin: 0, minWidth: '10rem' }}>
          <span className="small">Kind</span>
          <SearchableSelect className="form-select form-select-sm" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="site">Sites</option>
            <option value="engine">ViibCMS</option>
            <option value="other">Other apps</option>
          </SearchableSelect>
        </label>
        <label className="field" style={{ margin: 0, minWidth: '10rem' }}>
          <span className="small">Environment</span>
          <SearchableSelect className="form-select form-select-sm" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="live">Live</option>
            <option value="staging">Staging</option>
          </SearchableSelect>
        </label>
        <label className="field" style={{ margin: 0, minWidth: '12rem' }}>
          <span className="small">Time limit</span>
          <SearchableSelect className="form-select form-select-sm" value={timeoutMs} onChange={(e) => setTimeoutMs(Number(e.target.value))}>
            {TIMEOUTS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SearchableSelect>
        </label>
      </div>

      <div className="docker-projects" style={{ marginTop: '1rem' }}>
        {loading && apps.length === 0 ? <p className="muted">Loading…</p> : null}
        {!loading && visible.length === 0 ? (
          <p className="muted">No npm apps matched. Check the PM2 API key in Integrations.</p>
        ) : null}
        {visible.map((app) => {
          const folder = String(app.folder || '');
          const open = openFolder === folder;
          const scripts = scriptEntries(app.packageJson);
          const pkg = app.packageJson;
          const kind = pkg?.kind || 'other';
          const versionRow = versions[folder];
          const siteUrl = typeof app.siteUrl === 'string' ? app.siteUrl : '';
          const stage = siteStage(siteUrl);
          const down = health[folder]?.ok === false;
          const host = siteHost(siteUrl);
          return (
            <div key={folder} className={`docker-project${open ? ' is-open' : ''}`}>
              <div className="docker-project-header">
                <button type="button" className="docker-project-toggle" onClick={() => setOpenFolder(open ? '' : folder)}>
                  <span className="docker-project-chevron" aria-hidden>
                    {open ? '▾' : '▸'}
                  </span>
                  <span className="docker-project-name">{folder}</span>
                  <span className={`npm-kind npm-kind-${kind}`}>{KIND_LABEL[kind] || 'App'}</span>
                </button>
                {siteUrl && stage ? (
                  <span className="npm-site-row">
                    <span
                      className={`npm-stage npm-stage-${down ? 'down' : stage}`}
                      title={down ? 'Site did not respond' : siteUrl}
                    >
                      {down ? 'Down' : stage === 'live' ? 'Live' : 'Staging'}
                    </span>
                    <a className="npm-site-link" href={siteUrl} target="_blank" rel="noreferrer" title={siteUrl}>
                      {host || siteUrl}
                    </a>
                  </span>
                ) : null}
                <div className="npm-version-row">
                  <VersionControl
                    label="Site"
                    version={versionRow?.project}
                    updating={runningKey === `version:${folder}:project`}
                    disabled={busy}
                    onUpdate={() => void updateVersion(folder, 'project', versionRow?.project)}
                  />
                  <VersionControl
                    label="Engine"
                    version={versionRow?.engine}
                    updating={runningKey === `version:${folder}:engine`}
                    disabled={busy}
                    onUpdate={() => void updateVersion(folder, 'engine', versionRow?.engine)}
                  />
                  {!versionRow && versionsLoading ? <span className="muted small">versions…</span> : null}
                </div>
              </div>
              {open ? (
                <div className="npm-script-list">
                  {pkg == null ? (
                    <p className="muted small" style={{ margin: 0 }}>
                      This folder has no readable package.json.
                    </p>
                  ) : null}
                  {pkg && scripts.length === 0 ? (
                    <p className="muted small" style={{ margin: 0 }}>
                      package.json has no scripts.
                    </p>
                  ) : null}
                  {scripts.map(([name, command]) => {
                    const spec = scriptSpec(pkg, name);
                    const params = spec?.params || [];
                    const values = drafts[`${folder}::${name}`] || {};
                    const preview = collectArgs(params, values).display;
                    const running = runningKey === `npm:${folder}:${name}`;
                    return (
                      <form
                        key={name}
                        className="npm-script-block"
                        onSubmit={(event) => {
                          event.preventDefault();
                          void runScript(folder, name, params);
                        }}
                      >
                        <div className="npm-script-row">
                          <div className="npm-script-copy">
                            <strong>{name}</strong>
                            <code title={command}>{command}</code>
                          </div>
                          <button type="submit" className="btn btn-sm btn-outline-primary" disabled={busy}>
                            {running ? 'Running…' : 'Run'}
                          </button>
                        </div>
                        {params.length ? (
                          <div className="npm-script-form">
                            {params.map((param) => {
                              const id = `${folder}-${name}-${param.name}`;
                              if (!param.value) {
                                return (
                                  <label key={param.name} className="npm-script-check" htmlFor={id}>
                                    <input
                                      id={id}
                                      type="checkbox"
                                      checked={Boolean(values[param.name])}
                                      disabled={busy}
                                      onChange={(e) => setParam(folder, name, param.name, e.target.checked)}
                                    />
                                    <span>
                                      {humanize(param.name)}
                                      {param.flags?.[0] ? <code> {param.flags[0]}</code> : null}
                                    </span>
                                  </label>
                                );
                              }
                              return (
                                <label key={param.name} className="field" htmlFor={id}>
                                  <span className="small">
                                    {humanize(param.name)}
                                    {param.required ? ' *' : ''}
                                  </span>
                                  <FormInput
                                    id={id}
                                    type={param.secret ? 'password' : 'text'}
                                    value={values[param.name] || ''}
                                    autoComplete={param.secret ? 'new-password' : 'off'}
                                    placeholder={param.flags?.[0] || param.name}
                                    required={Boolean(param.required)}
                                    disabled={busy}
                                    onChange={(e) => setParam(folder, name, param.name, e.target.value)}
                                  />
                                </label>
                              );
                            })}
                          </div>
                        ) : null}
                        {preview.length ? (
                          <p className="muted small npm-script-preview">npm run {name} -- {preview.join(' ')}</p>
                        ) : null}
                        {spec?.waitsForInput ? (
                          <p className="muted small npm-script-preview">Required values must be filled in. Otherwise the command waits for a prompt and the API rejects it.</p>
                        ) : null}
                      </form>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {output ? (
        <div className="card-panel" style={{ marginTop: '1.25rem' }}>
          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{output.title}</h2>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOutput(null)}>
              Close
            </button>
          </div>
          {output.exitCode != null || output.timedOut || output.cwd ? (
            <p className="muted small" style={{ marginTop: '0.5rem' }}>
              {output.exitCode != null ? `Exit ${output.exitCode}` : ''}
              {output.timedOut ? ' · timed out' : ''}
              {output.cwd ? ` · ${output.cwd}` : ''}
            </p>
          ) : null}
          <h3 className="small" style={{ marginTop: '0.75rem' }}>
            stdout
          </h3>
          <pre className="npm-script-output">{output.stdout || 'No stdout.'}</pre>
          <h3 className="small" style={{ marginTop: '0.75rem' }}>
            stderr
          </h3>
          <pre className="npm-script-output">{output.stderr || 'No stderr.'}</pre>
        </div>
      ) : null}
    </div>
  );
}
